import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const StockEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate("shop/stocks", token);

  // Fetch shops and purchase items for selection
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );
  const { data: purchaseItemsData, loading: purchaseItemLoading } = useFetchObjects(
    ["purchase_item"],
    "shop/purchase_items",
    token
  );

  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];
  const purchaseItems = Array.isArray(purchaseItemsData) ? purchaseItemsData : purchaseItemsData?.results || [];

  const [selectedShop, setSelectedShop] = useState("");
  const [selectedPurchaseItem, setSelectedPurchaseItem] = useState("");
  const [quantity, setQuantity] = useState("");

  // Set initial values from editData.
  useEffect(() => {
    if (editData) {
      setSelectedShop(
        editData.shop && editData.shop.id
          ? String(editData.shop.id)
          : editData.shop
          ? String(editData.shop)
          : ""
      );
      setSelectedPurchaseItem(
        editData.purchase_item && editData.purchase_item.id
          ? String(editData.purchase_item.id)
          : editData.purchase_item
          ? String(editData.purchase_item)
          : ""
      );
      setQuantity(editData.quantity);
    }
  }, [editData]);

  // Validate shop selection once shops load.
  useEffect(() => {
    if (editData && shops && shops.length > 0) {
      const shopId =
        editData.shop && editData.shop.id
          ? String(editData.shop.id)
          : editData.shop
          ? String(editData.shop)
          : "";
      const exists = (shops || []).find((s) => String(s.id) === shopId);
      if (exists) {
        setSelectedShop(shopId);
      } else {
        setSelectedShop("");
      }
    }
  }, [editData, shops]);

  // Validate purchase item selection once purchase items load.
  useEffect(() => {
    if (editData && purchaseItems && purchaseItems.length > 0) {
      const itemId =
        editData.purchase_item && editData.purchase_item.id
          ? String(editData.purchase_item.id)
          : editData.purchase_item
          ? String(editData.purchase_item)
          : "";
      const exists = (purchaseItems || []).find(
        (item) => String(item.id) === itemId
      );
      if (exists) {
        setSelectedPurchaseItem(itemId);
      } else {
        setSelectedPurchaseItem("");
      }
    }
  }, [editData, purchaseItems]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("shop", selectedShop);
    formData.append("purchase_item", selectedPurchaseItem);
    formData.append("quantity", quantity);
    handleUpdate(editData.id, formData);
  };

  useEffect(() => {
    if (isSuccess) {
      setSelectedShop("");
      setSelectedPurchaseItem("");
      setQuantity("");
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("stock.editStock")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("stock.selectShop")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (shops || []).map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("stock.selectPurchaseItem")}
            fullWidth
            required
            value={selectedPurchaseItem}
            onChange={(e) => setSelectedPurchaseItem(e.target.value)}
          >
            {purchaseItemLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (purchaseItems || []).map((item) => (
                <MenuItem key={item.id} value={String(item.id)}>
                  {item.product && item.product.name
                    ? item.product.name
                    : t("stock.unknownPurchaseItem")}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            margin="dense"
            label={t("stock.quantity")}
            type="number"
            fullWidth
            required
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("stock.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading || !selectedShop || !selectedPurchaseItem || !quantity
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("stock.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockEdit;
